import React, { useCallback, useEffect, useRef, useState } from "react";
import { Responsive } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

/**
 * Responsive grid that tracks its own container width via ResizeObserver.
 *
 * react-grid-layout's WidthProvider only listens to window resize. Sidebar
 * expand/collapse changes .main-content width without resizing the window
 * (and CSS margin transitions finish after the Redux class toggle), so the
 * stock WidthProvider leaves a dead band on the right. Observing the grid
 * element itself keeps the layout filling the available width.
 */
export function ResponsiveGridLayout({ className, style, children, ...rest }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(1280);

  const measure = useCallback(() => {
    const node = containerRef.current;
    if (node instanceof HTMLElement && node.offsetWidth) {
      setWidth((prev) => (prev === node.offsetWidth ? prev : node.offsetWidth));
    }
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    measure();

    let frame = 0;
    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleMeasure)
        : null;

    if (observer) {
      observer.observe(node);
    } else {
      window.addEventListener("resize", measure);
    }

    return () => {
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", measure);
    };
  }, [measure]);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <Responsive width={width} className={className} style={style} {...rest}>
        {children}
      </Responsive>
    </div>
  );
}
